<template>
	<AsiListTableLayout :has-filter="hasFilter" :hide-search="true" :searchable="false" @clearSearch="clearFilter()">

		<template v-slot:action-buttons>
			<div class="d-flex flex-row ml-auto justify-end" style="width: 100%">
				<v-tooltip top>
					<template v-slot:activator="{on, attrs}">
						<v-btn icon v-bind="attrs" v-on="on" @click="reload(true, true)" class="mr-2">
							<v-icon>{{ icons.reload }}</v-icon>
						</v-btn>
					</template>
					<span>{{ $t('ui.reload') }}</span>
				</v-tooltip>
			</div>
		</template>

		<AsiContentContainer no-bottom-margin>
			<v-data-table
				:footer-props="{itemsPerPageOptions: perPageOptions}"
				:headers="createHeaders()"
				:items="items"
				:loading="loadingInternal"
				:options.sync="tableOptions"
				:server-items-length="total"
				item-key="id"
				multi-sort>

				<template v-slot:item.state="{item}">
					<span class="flex flex-row items-center">
						<v-icon v-if="isMailError(item)" size="16" color="red" class="mr-2">{{ icons.exclamationMark }}</v-icon>
						<v-icon v-if="isMailAlreadySent(item)" size="16" color="green" class="mr-2">{{ icons.checkMark }}</v-icon>
						<v-icon v-if="isMailDiscarded(item)" size="16" class="mr-2">{{ icons.archive }}</v-icon>
						{{ mailQueueEntryStateLabel(item.state) }}
					</span>
				</template>

				<template v-slot:item.createdAt="{item}">
					{{ $d(new Date(item.createdAt), 'short') }}
				</template>

				<template v-slot:item.sentAt="{item}">
					{{ item.sentAt ? $d(new Date(item.sentAt), 'short') : '' }}
				</template>

				<template v-slot:item.errorMessage="{item}">
					<v-tooltip top>
						<template v-slot:activator="{on, attrs}">
							<span v-on="on" v-bind="attrs" class="text-overflow-clamp">{{ item.errorMessage }}</span>
						</template>
						{{ item.errorMessage }}
					</v-tooltip>
				</template>

				<template v-slot:item.recipients="{item}">
					<v-tooltip top>
						<template v-slot:activator="{on, attrs}">
							<span v-on="on" v-bind="attrs" class="text-overflow-clamp">{{ item.recipients.join(', ') }}</span>
						</template>
						{{ item.recipients.join(', ') }}
					</v-tooltip>
				</template>

				<template v-if="showActions" v-slot:item.actions="{item}">
					<div class="d-flex flex-row flex-wrap justify-end">
							<v-tooltip top>
								<template v-slot:activator="{on, attrs}">
									<span v-bind="attrs" v-on="on">
										<AsiBtn :disabled="isMailAlreadySent(item)" :icon="icons.reload" small @click="onResend(item)"/>
									</span>
								</template>
								{{ $t('mailQueue.terms.resend') }}
							</v-tooltip>
							<v-tooltip top>
								<template v-slot:activator="{on, attrs}">
										<span v-bind="attrs" v-on="on">
											<AsiBtn :disabled="isMailAlreadySent(item) || isMailDiscarded(item)" :icon="icons.archive" small @click="onDiscard(item)"/>
										</span>
								</template>
								{{ $t('mailQueue.terms.discard') }}
							</v-tooltip>
					</div>
				</template>
			</v-data-table>
		</AsiContentContainer>

		<template v-slot:advanced-filters>
			<AsiSelectSimple v-model="filterModel.state" :items="mailQueueEntryStateOptions" :label="$t('mailQueue.state')" :placeholder="$t('mailQueue.state')" clearable/>
		</template>
	</AsiListTableLayout>
</template>

<script lang="ts">
	import {Component, Prop, Watch} from "vue-property-decorator";
	import AsiListTable from "@/components/common/AsiListTable.vue";
	import {IMailQueueAdminListEntry, MailQueueEntryState} from "@/models/mail-queue/MailQueueAdminModels";
	import MailQueueAdminListFilter from "@/models/mail-queue/MailQueueAdminListFilter";
	import IAsiListTableImplementation from "@/components/common/IAsiListTableImplementation";
	import AsiListTableHeader from "@/components/common/AsiListTableHeader";
	import AsiListTableOptions from "@/components/common/AsiListTableOptions";
	import AsiListTableLayout from "@/components/common/AsiListTableLayout.vue";
	import IPaginatedResponse from "@/models/IPaginatedResponse";
	import AsiContentContainer from "@/components/common/AsiContentContainer.vue";
	import Icon from "@/plugins/icons";
	import AsiSelectSimple from "@/components/common/AsiSelectSimple";
	import EnumHelper from "@/helpers/EnumHelper";
	import Snackbar from "@/helpers/Snackbar";
	import {TranslateResult} from "vue-i18n";

	@Component({
		components: {
			AsiSelectSimple,
			AsiContentContainer,
			AsiListTableLayout,
			AsiListTable
		}
	})
	export default class MailQueueList extends AsiListTable<IMailQueueAdminListEntry, MailQueueAdminListFilter> implements IAsiListTableImplementation<IMailQueueAdminListEntry, MailQueueAdminListFilter> {

		@Prop({type: Boolean, default: true})
		public showActions!: boolean;

		private icons = Icon;
		private initialized: boolean = false;

		public fetchData(filterModel: MailQueueAdminListFilter | null, tableOptions: AsiListTableOptions | null): Promise<IPaginatedResponse<IMailQueueAdminListEntry>> {
			return this.$mailQueueServiceAdmin.mailQueueEntries(filterModel, tableOptions);
		}

		public created(): void {
			this.reload(false, false);
			this.$nextTick(() => this.initialized = true);
		}

		protected prepareOptions(options: AsiListTableOptions): AsiListTableOptions {
			if (options.sortBy.length === 0 && this.defaultSortBy.length === 0) {
				options.sortBy = ['createdAt'];
				options.sortDesc = [false];
			}
			return options;
		}

		protected clearFilter(): void {
			this.filterModel?.clear();
		}

		public reload(goToFirstPage: boolean = false, debounce: boolean = true): void {
			this.reloadInternal(this, goToFirstPage, debounce);
		}

		public openModelDetailPage(): void {
			//do nothing...
		}

		public createFilterModel(): MailQueueAdminListFilter {
			return new MailQueueAdminListFilter();
		}

		private get mailQueueEntryStateOptions(): { text: string, value: string | number }[] {
			return EnumHelper.toSelectItems(MailQueueEntryState, true);
		}

		private mailQueueEntryStateLabel(state: MailQueueEntryState):  string | TranslateResult | null {
			return EnumHelper.textFromValue(MailQueueEntryState, state, true);
		}

		private isMailAlreadySent(item: IMailQueueAdminListEntry): boolean {
			return item.state == MailQueueEntryState.mailQueueEntrySent;
		}

		private isMailError(item: IMailQueueAdminListEntry): boolean {
			return item.state == MailQueueEntryState.mailQueueEntryError;
		}

		private isMailDiscarded(item: IMailQueueAdminListEntry): boolean {
			return item.state == MailQueueEntryState.mailQueueEntryDiscarded;
		}

		private onDiscard(item: IMailQueueAdminListEntry): void {
			this.$mailQueueServiceAdmin.updateMailQueueEntryState(item.id, MailQueueEntryState.mailQueueEntryDiscarded)
				.then(() => Snackbar.show(this.$t('mailQueue.terms.entryDiscarded'), 'info', 1000))
				.then(() => this.reload(true))
			;
		}

		private onResend(item: IMailQueueAdminListEntry): void {
			this.$mailQueueServiceAdmin.updateMailQueueEntryState(item.id, MailQueueEntryState.mailQueueEntryCreated)
				.then(() => Snackbar.show(this.$t('mailQueue.terms.entryResending'), 'success', 1000))
				.then(() => this.reload(true));
		}

		public createHeaders(): AsiListTableHeader[] {
			const ret: AsiListTableHeader[] = [
				new AsiListTableHeader(this.$t('mailQueue.createdAt'), 'createdAt', true, false, 'start','10%'),
				new AsiListTableHeader(this.$t('mailQueue.sentAt'), 'sentAt', false, false, 'start', '10%'),
				new AsiListTableHeader(this.$t('mailQueue.state'), 'state', false, true, 'start', '10%'),
				new AsiListTableHeader(this.$t('mailQueue.errorMessage'), 'errorMessage', false, true, 'start'),
				new AsiListTableHeader(this.$t('mailQueue.subject'), 'subject', false, false, 'start', '15%'),
				new AsiListTableHeader(this.$t('mailQueue.recipients'), 'recipients', false, false, 'start'),
			];

			if (this.showActions) {
				ret.push(
					new AsiListTableHeader('', 'actions', false, false, 'end', '6rem'),
				);
			}

			return ret;
		}

		@Watch('filterModel', {deep: true})
		private onFilterModelChanged(): void {
			if (!this.initialized) return;
			this.reload(true);
		}

		@Watch('tableOptions', {deep: true})
		private onTableOptionsChanged(): void {
			if (!this.initialized) return;
			this.reload();
		}

	}
</script>

<style scoped lang="scss">
	.text-overflow-clamp {
		overflow: hidden;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 1;
		display: -webkit-box;
	}
</style>